import { Comment as AntMessage, Image, Spin, Tag } from 'antd';
import Avatar from '@components/Avatar';
import { FunctionComponent, useCallback, useMemo, useState } from 'react';
import css from './TribeMessage.module.less';
import { Link } from 'react-router-dom';
import { getPath } from '@helpers/navigation';
import moment from 'moment';
import useTranslate from '@hooks/useTranslate';
import componentContent from './TribesMessage.content.json';
import { FULL_TIME_FORMAT } from '@config/format';
import Threads from './Threads/ThreadsList';
import { useStore } from 'effector-react';
import threads, { getList, reset } from '@stores/threads';
import { IChatAttachment, ITribesChatDetailMessages } from '@interfaces/ITribes';
import ROLES from '@constants/roles';
import authStore from '@stores/auth';
import chatDetail, { deleteMessage } from '@stores/chatDetail';
import tribesMembers, { banMember } from '@stores/tribesMembers';

interface TribeMessageProps {
  message: ITribesChatDetailMessages;
  className?: string;
  chatId?: string;
  tribeId?: string;
  ownerId?: string;
  isMemberDeleted?: boolean;
}

function getImageUrls(attachments: IChatAttachment[] = []): string[] {
  const urls = attachments
    .map((a) => {
      const direct = a.url || a.image_url || a.thumb_url || a.asset_url;

      if (typeof direct === 'string' && /^https?:\/\//.test(direct)) return direct;

      if (typeof a.asset_url === 'string') {
        try {
          const parsed = JSON.parse(a.asset_url);
          const parsedUrl = parsed?.url || parsed?.presetUrlTemplate;
          if (typeof parsedUrl === 'string' && /^https?:\/\//.test(parsedUrl)) {
            return parsedUrl;
          }
        } catch {
          /* ignore */
        }
      }

      return null;
    })
    .filter(Boolean) as string[];

  return Array.from(new Set(urls));
}

const TribeMessage: FunctionComponent<TribeMessageProps> = ({
  message,
  className,
  chatId,
  tribeId,
  ownerId,
  isMemberDeleted,
}) => {
  const auth = useStore(authStore);

  const isAdmin =
    auth?.user?.role === ROLES.ADMIN ||
    auth?.user?.role === ROLES.COMMUNITY_MANAGER ||
    auth?.user?.role === ROLES.MODERATOR;
  const content = useTranslate(componentContent);

  const { preloaders: threadsPreloaders } = useStore(threads);
  const { preloaders: chatPreloaders } = useStore(chatDetail);
  const { preloaders: membersPreloaders } = useStore(tribesMembers);

  const [threadsList, setThreads] = useState<ITribesChatDetailMessages[]>([]);
  const { threadInfo }: ITribesChatDetailMessages = message;

  const isThreadsLoading = !!threadsPreloaders.getList;
  const isDeleteLoading = !!chatPreloaders.deleteMessage;
  const isBanLoading = !!membersPreloaders.banMember;

  const isOwnerMessage = ownerId != null && String(message.user.id) === String(ownerId);

  const isDeletedByMod = Boolean(message.msgDeleted);

  const messageText = useMemo(() => {
    if (message?.contentMessage?.type === 'gif' && message?.contentMessage?.gifUrl)
      return (
        <>
          <a href={message.contentMessage.gifUrl}>{message.contentMessage.gifUrl}</a>
          <br />
          <Image
            src={message.contentMessage.gifUrl}
            width={100}
          />
        </>
      );
    if (message?.contentMessage?.type === 'gif') return 'GIF';
    return message.text;
  }, [message?.contentMessage]);

  const images = useMemo(() => getImageUrls(message?.attachments), [message]);

  const handleOpenThreads = useCallback(
    (value: boolean) => {
      const threadId = threadInfo?.threadId;
      if (value && threadId) {
        reset();
        getList(threadId).then(({ data }) => {
          setThreads(data.body.messages);
        });
      }
    },
    [threadInfo],
  );

  const onDelete = () => {
    if (isDeleteLoading) return;
    if (!chatId || !message?.id) return;

    deleteMessage({ chatId, messageId: message.id });
  };

  const onBan = () => {
    if (isBanLoading) return;
    const accountId = message?.user?.id;
    if (!tribeId || !accountId) return;

    banMember({ tribeId, accountId });
  };

  return (
    <AntMessage
      key={message.id}
      className={className ? css.thread : css.message}
      author={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <Link
            to={getPath('userDetail', { userId: message.user.id })}
            className='clickable'
          >
            {message.user.name}
          </Link>

          {isMemberDeleted && !isOwnerMessage && <Tag color='red'>{content.member_removed}</Tag>}
        </span>
      }
      avatar={<Avatar user={message.user} />}
      content={
        <div className={css.body}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <span className={css.text}>
                {isDeletedByMod ? (
                  <Tag color='red'>{content.deleted_by_moderation}</Tag>
                ) : (
                  messageText
                )}
              </span>

              {images.length > 0 ? (
                <div className={css.images}>
                  {images.map((src) => (
                    <Image
                      key={src}
                      src={src}
                      width={160}
                    />
                  ))}
                </div>
              ) : null}
            </div>

            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <div className={css.metaLine}>
                {chatId && isAdmin && (
                  <span className={css.metaLineItem}>
                    <span
                      className='clickable'
                      onClick={onDelete}
                    >
                      {!isDeletedByMod &&
                        (isDeleteLoading ? <Spin size='small' /> : content.delete_message)}
                    </span>
                  </span>
                )}

                {chatId && tribeId && !isOwnerMessage && isAdmin && !isMemberDeleted && (
                  <span className={css.metaLineItem}>
                    <span
                      className='clickable'
                      onClick={onBan}
                    >
                      {isBanLoading ? <Spin size='small' /> : content.exclude_participant}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>

          {threadInfo ? (
            <Threads
              messages={threadsList}
              isListLoading={isThreadsLoading}
              onOpen={(event) => handleOpenThreads(event)}
            />
          ) : null}
        </div>
      }
      datetime={moment(message.created_at).format(FULL_TIME_FORMAT)}
    />
  );
};

export default TribeMessage;
